import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Hammer,
  RefreshCw,
  RotateCwSquare,
  Settings2Icon,
  TriangleAlert,
} from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  AssignmentViewMode,
  getEffectiveAbilityCustomGrouping,
  getEffectiveAssignmentGroups,
  getEffectiveAssignmentViewMode,
  getAssignmentScopes,
  normalizeAssignmentBox,
  normalizeAssignmentCast,
} from '../../TeamPlans/components/assignmentTimeline'
import AssignmentEntityChip from '../../TeamPlans/components/AssignmentEntityChip'
import { getAssignmentEntityLabel } from '../../TeamPlans/components/assignmentEntityLabels'
import SpellIcon from '../../TeamPlans/components/SpellIcon'
import {
  AssignmentPhaseTimestampBadge,
  buildAssignmentPhaseTimestampBadgeData,
  PhaseDivider,
} from '../../TeamPlans/components/AssignmentPhaseTimestampBadge'
import { EncounterAbilityRow } from '../../TeamPlans/hooks/useTeamPlanComputedData'
import { RaidPlanEncounterAssignmentAbility } from 'app/frontend/types/custom/RaidPlan'
import {
  ResolvedAssignmentEntity,
  ResolvedAssignmentParticipant,
} from './eventActivityTimelineResolver'
import { classColours } from 'app/frontend/types/custom/CharacterClass'
import Card from 'app/frontend/base/components/Card'
import EventPlanOverridePopover from './EventPlanOverridePopover'
import { defaultAggregateOptions } from './EventPlanOverridePopover'
import { renderResolvedAssignmentEntityLabel } from './renderResolvedAssignmentEntityLabel'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import ProtectedContent from 'app/frontend/base/components/ProtectedContent'
import { EncounterAssignmentEntityOverride } from './eventActivityPlanOverrides'
import { useTeam } from 'app/frontend/base/config/context/team'
import RaidstratsContainer from '../../TeamPlans/components/RaidstratsContainer'
import { getClass } from 'app/frontend/base/services/characterValues'
import { usePageInfo } from 'app/frontend/base/config/queries'
import { cn } from '@/lib/utils'
import { useAuth } from 'app/frontend/base/config/context/auth'

export type EventPlanOverrideSelection = {
  abilityKey: string
  castIds: string[]
  boxId: string
  entityId: string
  override: EncounterAssignmentEntityOverride
}

type Props = {
  row: EncounterAbilityRow
  ability: RaidPlanEncounterAssignmentAbility
  phaseDividers: PhaseDivider[]
  resolvedByScopeBoxKey: Record<string, ResolvedAssignmentEntity[]>
  selectedParticipants: ResolvedAssignmentParticipant[]
  onOverrideSelect: (selection: EventPlanOverrideSelection) => void
  onResetAbilityOverrides: (abilityKey: string) => void
  onSetAbilityHidden: (abilityKey: string, hidden: boolean) => void
  isAbilityHidden?: boolean
  isReadonly?: boolean
}

const extractUuid = (value: string) => {
  const match = value.match(
    /[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}/i,
  )
  return match?.[0] ?? null
}

const EventActivityPlanAbilityRow = ({
  row,
  ability,
  phaseDividers,
  resolvedByScopeBoxKey,
  selectedParticipants,
  onOverrideSelect,
  onResetAbilityOverrides,
  onSetAbilityHidden,
  isAbilityHidden = false,
  isReadonly = false,
}: Props) => {
  const { t } = useTranslation()
  const { team } = useTeam()
  const { accessLevel } = useAuth()
  const {
    data: { user },
  } = usePageInfo()
  const [showRaidPlan, setShowRaidPlan] = useState(true)
  const [raidPlanReloadVersionByScopeId, setRaidPlanReloadVersionByScopeId] =
    useState<Record<string, number>>({})
  const abilityName = ability.title ?? row.name
  const abilityViewMode: AssignmentViewMode = getEffectiveAssignmentViewMode(
    ability.view_mode,
  )
  const abilityCustomGrouping = getEffectiveAbilityCustomGrouping(
    ability.spell_id,
    ability.custom_grouping,
  )
  const assignmentGroups = getEffectiveAssignmentGroups(
    row.assignmentGroups,
    ability.assignment_groups,
  )
  const scopes = getAssignmentScopes(
    row.entries,
    abilityViewMode,
    t,
    abilityCustomGrouping,
  )
  const raidPlanValue = (ability.raid_plan_link ?? '').trim()
  const raidPlanId = extractUuid(raidPlanValue)
  const hasValidRaidPlanId = Boolean(raidPlanId)
  const selectedParticipantByCharacterId = (team.members ?? []).reduce<
    Record<number, wowaudit.TeamMembership>
  >((acc, member) => {
    acc[member.characterReference.id] = member
    return acc
  }, {})
  const participantClassByCharacterId = Object.fromEntries(
    Object.entries(selectedParticipantByCharacterId).map(
      ([characterId, member]) => [
        Number(characterId),
        member.characterReference.characterClass,
      ],
    ),
  )
  const aggregateOptions: Extract<
    EncounterAssignmentEntityOverride,
    { kind: 'aggregate' }
  >[] = defaultAggregateOptions.map((option) => ({
    kind: 'aggregate',
    entity: option.entity,
  }))
  const alternatingParticipantEntityKeysByCharacterId: Record<
    number,
    string[]
  > = {}
  const alternatingBlockedCharacterIds = new Set<number>()
  if (abilityViewMode === 'alternating') {
    scopes.forEach((scope) => {
      for (let boxIndex = 0; boxIndex < assignmentGroups; boxIndex += 1) {
        const key = `${row.abilityKey}:${scope.id}:${boxIndex}`
        const resolvedEntities = resolvedByScopeBoxKey[key] ?? []
        resolvedEntities.forEach((resolvedEntity, entityIndex) => {
          if (resolvedEntity.kind !== 'participant') return
          const characterId = resolvedEntity.participant.characterId
          alternatingBlockedCharacterIds.add(characterId)
          if (!alternatingParticipantEntityKeysByCharacterId[characterId]) {
            alternatingParticipantEntityKeysByCharacterId[characterId] = []
          }
          alternatingParticipantEntityKeysByCharacterId[characterId].push(
            `${scope.id}:${boxIndex}:${entityIndex}`,
          )
        })
      }
    })
  }
  const getIssueMessage = (entity: ResolvedAssignmentEntity) => {
    if (!entity.issue) return null
    if (entity.issue.type === 'insufficient_candidates') {
      const roleLabel =
        entity.issue.role === 'anyone' ? 'player' : entity.issue.role
      const slotLabel =
        roleLabel ??
        (entity.sourceEntity.kind === 'by_class'
          ? getClass(entity.sourceEntity.class_id.toString()) ?? 'class'
          : 'player')
      return `No matching ${slotLabel} found for this slot (${entity.issue.available} available).`
    }
    return `No match found for ${
      entity.issue.memberName ??
      getAssignmentEntityLabel(t, entity.sourceEntity)
    }.`
  }
  const getEntityChipLabel = (entity: ResolvedAssignmentEntity) => {
    return renderResolvedAssignmentEntityLabel({
      t,
      entity,
      participantClassByCharacterId,
    })
  }
  const abilityActionsMenu = isReadonly ? null : (
    <ProtectedContent
      requirement='admin'
      section='events'
      renderUnauthorized={false}
    >
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant='outline' size='xs'>
            <Settings2Icon className='h-3.5 w-3.5' />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align='end'>
          <DropdownMenuItem
            onSelect={() => {
              onResetAbilityOverrides(row.abilityKey)
            }}
          >
            Reset changes
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={() => {
              onSetAbilityHidden(row.abilityKey, !isAbilityHidden)
            }}
          >
            {isAbilityHidden ? 'Show' : 'Hide'}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </ProtectedContent>
  )
  const handleReloadRaidPlan = () => {
    setShowRaidPlan(true)
    setRaidPlanReloadVersionByScopeId((prev) =>
      scopes.reduce<Record<string, number>>(
        (next, scope) => {
          next[scope.id] = (prev[scope.id] ?? 0) + 1
          return next
        },
        { ...prev },
      ),
    )
  }
  const isCustomAbility = !/^\d+$/.test(row.abilityKey)

  if (isAbilityHidden) {
    return (
      <ProtectedContent
        requirement='admin'
        section='events'
        renderUnauthorized={false}
      >
        <Card className='mb-3 py-3'>
          <div className='flex items-center gap-2 text-sm font-medium'>
            <span className='truncate'>{abilityName}</span>
            <Badge variant='outline'>Hidden</Badge>
            <div className='grow flex justify-end text-foreground'>
              {abilityActionsMenu}
            </div>
          </div>
        </Card>
      </ProtectedContent>
    )
  }

  return (
    <Card className='mb-3 py-3'>
      <div
        className='flex items-center gap-2 text-sm font-medium mb-2 flex-wrap'
        style={{ color: row.color }}
      >
        <div
          className='flex items-center gap-2 min-w-0'
          style={{ color: row.color }}
        >
          <SpellIcon
            spellId={row.spellId}
            className='w-6 h-6 rounded border border-border'
          />
          {isCustomAbility ? (
            <span className='truncate'>{abilityName}</span>
          ) : (
            <a
              href={`https://wowhead.com/spell=${row.spellId}`}
              target='_blank'
              rel='noreferrer'
              className='truncate'
              style={{ color: row.color }}
            >
              {abilityName}
            </a>
          )}
        </div>
        {scopes.length === 1 ? (
          <div className='flex flex-wrap items-center gap-1 ml-1 text-xs text-muted-foreground'>
            {scopes[0].castIndexes.map((castIndex) => (
              <AssignmentPhaseTimestampBadge
                key={castIndex}
                {...buildAssignmentPhaseTimestampBadgeData({
                  castIndexes: [castIndex],
                  entries: row.entries,
                  phaseDividers,
                })}
              />
            ))}
          </div>
        ) : null}

        <div className='grow flex items-center justify-end gap-2 text-foreground'>
          {hasValidRaidPlanId ? (
            <>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type='button'
                    variant='outline'
                    size='xs'
                    onClick={handleReloadRaidPlan}
                  >
                    <RefreshCw className='h-3.5 w-3.5' />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Fetch latest raid plan version</TooltipContent>
              </Tooltip>
              <Button
                type='button'
                variant='outline'
                size='xs'
                onClick={() => setShowRaidPlan((value) => !value)}
              >
                {showRaidPlan ? 'Hide visual' : 'View visual'}
              </Button>
            </>
          ) : null}
          {abilityActionsMenu}
        </div>
      </div>

      <div className='space-y-6'>
        {scopes.map((scope) => {
          const representativeCast = normalizeAssignmentCast(
            ability.casts?.[scope.castIndexes[0]],
            assignmentGroups,
          )
          const participantEntityKeysByCharacterId: Record<number, string[]> =
            {}
          const blockedCharacterIds = new Set<number>()
          for (let boxIndex = 0; boxIndex < assignmentGroups; boxIndex += 1) {
            const key = `${row.abilityKey}:${scope.id}:${boxIndex}`
            const resolvedEntities = resolvedByScopeBoxKey[key] ?? []
            resolvedEntities.forEach((resolvedEntity, entityIndex) => {
              if (resolvedEntity.kind !== 'participant') return
              const characterId = resolvedEntity.participant.characterId
              blockedCharacterIds.add(characterId)
              if (!participantEntityKeysByCharacterId[characterId]) {
                participantEntityKeysByCharacterId[characterId] = []
              }
              participantEntityKeysByCharacterId[characterId].push(
                `${scope.id}:${boxIndex}:${entityIndex}`,
              )
            })
          }
          const castIds = scope.castIndexes
            .map(
              (castIndex) =>
                normalizeAssignmentCast(
                  ability.casts?.[castIndex],
                  assignmentGroups,
                ).id,
            )
            .filter((castId): castId is string => Boolean(castId))
          const raidPlanReloadVersion =
            raidPlanReloadVersionByScopeId[scope.id] ?? 0
          return (
            <div key={scope.id}>
              {scopes.length > 1 ? (
                <div className='flex flex-wrap items-center gap-3 mb-1'>
                  <div className='font-medium text-sm'>{scope.label}</div>
                  <div className='flex flex-wrap items-center gap-1 text-xs text-muted-foreground'>
                    {scope.castGroups.map((castGroup, castGroupIndex) => (
                      <AssignmentPhaseTimestampBadge
                        key={`${scope.id}:${castGroupIndex}`}
                        {...buildAssignmentPhaseTimestampBadgeData({
                          castIndexes: castGroup,
                          entries: row.entries,
                          phaseDividers,
                        })}
                      />
                    ))}
                  </div>
                </div>
              ) : null}

              {showRaidPlan && raidPlanId ? (
                <div className='mb-3'>
                  <RaidstratsContainer
                    key={`${scope.id}-${raidPlanReloadVersion}`}
                    raidPlanId={raidPlanId}
                    players={Array.from(
                      { length: assignmentGroups },
                      (_, boxIndex) =>
                        resolvedByScopeBoxKey[
                          `${row.abilityKey}:${scope.id}:${boxIndex}`
                        ] ?? [],
                    )
                      .flat()
                      .flatMap((entity) =>
                        entity.kind === 'participant'
                          ? [
                              {
                                name: entity.participant.name,
                                className: entity.participant.characterClass,
                              },
                            ]
                          : [],
                      )}
                  />
                </div>
              ) : null}

              <div
                className='grid gap-2'
                style={{
                  gridTemplateColumns: `repeat(${Math.max(
                    Math.min(assignmentGroups, 4),
                    1,
                  )}, minmax(0, 1fr))`,
                }}
              >
                {Array.from({ length: assignmentGroups }, (_, boxIndex) => {
                  const key = `${row.abilityKey}:${scope.id}:${boxIndex}`
                  const resolvedEntities = resolvedByScopeBoxKey[key] ?? []
                  const box = normalizeAssignmentBox(
                    representativeCast.boxes[boxIndex],
                  )
                  return (
                    <div key={boxIndex} className='pt-1'>
                      {assignmentGroups > 1 ? (
                        <div className='text-xs border-b mb-1 pb-1'>
                          {t('dashboard.team.plans.assignments.group-label', {
                            count: boxIndex + 1,
                          })}
                        </div>
                      ) : null}
                      {resolvedEntities.length === 0 ? (
                        <div className='text-xs text-muted-foreground'>
                          No resolved assignments
                        </div>
                      ) : (
                        <div className='flex flex-wrap items-center gap-1'>
                          {resolvedEntities.map((entity, entityIndex) => {
                            const issueMessage = getIssueMessage(entity)
                            const sourceEntity = box.entities[entityIndex]
                            const boxId = box.id
                            const entityId = sourceEntity?.id
                            const hasOverride = entity.isOverridden === true
                            const hasMemberFallbackMatch =
                              sourceEntity?.kind === 'member' &&
                              entity.kind === 'participant' &&
                              entity.participant.characterId !==
                                sourceEntity.character_id
                            const sourceMemberParticipant =
                              sourceEntity?.kind === 'member'
                                ? selectedParticipantByCharacterId[
                                    sourceEntity.character_id
                                  ]
                                : undefined
                            const baseKey = `${
                              sourceEntity?.id ?? entityIndex
                            }-${entityIndex}`
                            const isParticipant = entity.kind === 'participant'
                            const isOwnCharacterEntity =
                              isParticipant &&
                              selectedParticipantByCharacterId[
                                entity.participant.characterId
                              ]?.characterReference.userId === user?.id
                            const chip = (
                              <AssignmentEntityChip
                                index={entityIndex}
                                className={cn(
                                  isOwnCharacterEntity &&
                                    'border-info-foreground',
                                  issueMessage
                                    ? isParticipant
                                      ? 'border-warning'
                                      : 'border-warning-foreground bg-warning hover:bg-warning'
                                    : undefined,
                                )}
                                endSlot={
                                  issueMessage ||
                                  hasOverride ||
                                  hasMemberFallbackMatch ? (
                                    <span className='inline-flex items-center gap-0.5'>
                                      {hasOverride ? (
                                        <Hammer className='h-4 w-4 text-info-foreground' />
                                      ) : issueMessage ? (
                                        <TriangleAlert className='h-4 w-4 text-warning-foreground' />
                                      ) : hasMemberFallbackMatch ? (
                                        <RotateCwSquare className='h-4 w-4 text-info-foreground' />
                                      ) : null}
                                    </span>
                                  ) : undefined
                                }
                                label={getEntityChipLabel(entity)}
                              />
                            )
                            const chipWithIssue =
                              !issueMessage &&
                              !hasOverride &&
                              !hasMemberFallbackMatch ? (
                                chip
                              ) : (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span>{chip}</span>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    {hasOverride ? (
                                      'Manually edited'
                                    ) : hasMemberFallbackMatch ? (
                                      <>
                                        Replaces{' '}
                                        <span
                                          className={
                                            sourceMemberParticipant
                                              ? classColours[
                                                  sourceMemberParticipant
                                                    .characterReference
                                                    .characterClass
                                                ].text
                                              : undefined
                                          }
                                        >
                                          {sourceEntity.label}
                                        </span>{' '}
                                        in the plan
                                      </>
                                    ) : (
                                      issueMessage
                                    )}
                                  </TooltipContent>
                                </Tooltip>
                              )
                            if (
                              isReadonly ||
                              !entityId ||
                              !boxId ||
                              !castIds.length
                            ) {
                              return <span key={baseKey}>{chipWithIssue}</span>
                            }
                            const currentEntityKey = `${scope.id}:${boxIndex}:${entityIndex}`
                            const entityKeysByCharacterIdForFilter =
                              abilityViewMode === 'alternating'
                                ? alternatingParticipantEntityKeysByCharacterId
                                : participantEntityKeysByCharacterId
                            const blockedCharacterIdsForFilter =
                              abilityViewMode === 'alternating'
                                ? alternatingBlockedCharacterIds
                                : blockedCharacterIds
                            const availableParticipants =
                              abilityViewMode === 'all_separate'
                                ? selectedParticipants
                                : selectedParticipants.filter((participant) => {
                                    if (
                                      !blockedCharacterIdsForFilter.has(
                                        participant.characterId,
                                      )
                                    ) {
                                      return true
                                    }
                                    const entityKeys =
                                      entityKeysByCharacterIdForFilter[
                                        participant.characterId
                                      ] ?? []
                                    return (
                                      entityKeys.length === 1 &&
                                      entityKeys[0] === currentEntityKey
                                    )
                                  })
                            const selectedOverride:
                              | EncounterAssignmentEntityOverride
                              | undefined = isParticipant
                              ? {
                                  kind: 'participant',
                                  characterId: entity.participant.characterId,
                                }
                              : entity.kind === 'aggregate' &&
                                (entity.sourceEntity.kind === 'everyone' ||
                                  entity.sourceEntity.kind === 'collection' ||
                                  entity.sourceEntity.kind === 'group')
                              ? {
                                  kind: 'aggregate',
                                  entity: entity.sourceEntity,
                                }
                              : undefined
                            return (
                              <EventPlanOverridePopover
                                key={baseKey}
                                trigger={chipWithIssue}
                                disabled={accessLevel !== 'admin'}
                                participants={availableParticipants}
                                aggregateOptions={aggregateOptions}
                                selectedOverride={selectedOverride}
                                onSelect={(override) =>
                                  onOverrideSelect({
                                    abilityKey: row.abilityKey,
                                    castIds,
                                    boxId,
                                    entityId,
                                    override,
                                  })
                                }
                              />
                            )
                          })}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          )
        })}
      </div>
    </Card>
  )
}

export default EventActivityPlanAbilityRow
